/**
 * BuildBadge — bottom-left chip showing the git SHA + age of the
 * currently-loaded JS bundle. Single source of truth for "is this my
 * change live or am I looking at a cached page?"
 *
 * Pattern mirrored from Taskalotl (docs/build-version-badge.md there).
 *
 * Values come from Vite-injected env vars (substituted into the bundle
 * at build time, no runtime fetch). Defaults to "dev" in local
 * development so it's obvious you're not on a deployed build.
 */
import React, { useEffect, useState } from 'react';
import './BuildBadge.css';

const RAW_SHA = (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? 'dev';
const RAW_BRANCH = (import.meta.env.VITE_BUILD_BRANCH as string | undefined) ?? 'local';
const RAW_TIME = (import.meta.env.VITE_BUILD_TIME as string | undefined) ?? '';

const SHORT_SHA = RAW_SHA.length > 8 ? RAW_SHA.slice(0, 7) : RAW_SHA;
const BUILD_MS = RAW_TIME ? Date.parse(RAW_TIME) : Number.NaN;
const IS_DEV = SHORT_SHA === 'dev';

function relativeAge(buildMs: number): string {
  if (Number.isNaN(buildMs)) return '';
  const ageSec = Math.max(0, Math.floor((Date.now() - buildMs) / 1000));
  if (ageSec < 60) return `${ageSec}s`;
  const ageMin = Math.floor(ageSec / 60);
  if (ageMin < 60) return `${ageMin}m`;
  const ageHr = Math.floor(ageMin / 60);
  if (ageHr < 24) return `${ageHr}h`;
  return `${Math.floor(ageHr / 24)}d`;
}

function formatBuildTime(buildMs: number): string {
  if (Number.isNaN(buildMs)) return '';
  const d = new Date(buildMs);
  // 2026-05-31 14:33:12 UTC — ISO-like but readable
  return `${d.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

export function BuildBadge(): React.JSX.Element {
  const [age, setAge] = useState(() => relativeAge(BUILD_MS));
  const [copied, setCopied] = useState(false);

  // Refresh the relative-age display every 30s. The build time itself
  // is static; this just keeps the "10m / 11m" tick alive while the
  // page is open.
  useEffect(() => {
    if (IS_DEV) return; // No age for dev
    const id = setInterval(() => setAge(relativeAge(BUILD_MS)), 30_000);
    return () => clearInterval(id);
  }, []);

  const onCopy = (): void => {
    if (IS_DEV) return;
    void navigator.clipboard?.writeText(RAW_SHA).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  const buildTimeLabel = formatBuildTime(BUILD_MS);
  const tooltip = IS_DEV
    ? 'Local dev build — not a deployed build'
    : `build  ${SHORT_SHA}\nbranch ${RAW_BRANCH}\ntime   ${buildTimeLabel}${age ? ` (${age} ago)` : ''}`;

  const isMaster = RAW_BRANCH === 'master' || RAW_BRANCH === 'main';

  return (
    <button
      type="button"
      className="build-badge"
      data-dev={IS_DEV || undefined}
      onClick={onCopy}
      title={tooltip}
      aria-label={`Build ${SHORT_SHA}${age ? `, ${age} ago` : ''} — click to copy SHA`}
    >
      <span className="build-badge-sha">{SHORT_SHA}</span>
      {!IS_DEV && !isMaster && <span className="build-badge-branch">{RAW_BRANCH}</span>}
      {!IS_DEV && age && (
        <span className="build-badge-age" aria-hidden="true">
          {age}
        </span>
      )}
      {copied && (
        <span className="build-badge-copied" aria-hidden="true">
          copied
        </span>
      )}
    </button>
  );
}
